import {
  EntityRefUtil,
  formatDateWithUserTimezone,
  formatTimeWithUserTimezone,
  GetScheduleFormsRequestDTO,
  GetScheduleFormsResponseDTO,
  RecordState,
} from '@gearbelt/global';
import { AccessorKeyColumnDef, createColumnHelper } from '@tanstack/react-table';

import { ASSET_SECTION_LABEL } from '@/components/table-page/columns/asset-tags-column';
import { Checkbox } from '@/components/ui/checkbox';
import { UserAvatarLabel } from '@/components/users/user-avatar-label';
import { initials } from '@/lib/utils';
import { ApiService } from '@/services/api-v2';
import { useUserStore } from '@/stores/user.store';
import { getAvatarBackgroundColorClass } from '@/utils/avatar';

const columnHelper = createColumnHelper<GetScheduleFormsResponseDTO['data'][number]>();

export const columns = () => {
  const userTimezone = useUserStore.getState().user?.timezone;
  return [
    columnHelper.display({
      id: 'select',
      header: ({ table }) => (
        <div className="flex w-full justify-center">
          <label className="-m-2 p-2">
            <Checkbox
              checked={table.getIsAllPageRowsSelected()}
              onCheckedChange={(value) => table.toggleAllPageRowsSelected(!!value)}
              aria-label="Select all"
            />
          </label>
        </div>
      ),
      cell: ({ row }) => (
        <div className="flex w-full justify-center">
          <label className="-m-2 p-2" onClick={(e) => e.stopPropagation()}>
            <Checkbox checked={row.getIsSelected()} onCheckedChange={(value) => row.toggleSelected(!!value)} aria-label="Select row" />
          </label>
        </div>
      ),
      meta: {
        cellClassName: 'w-[40px] min-w-[40px] max-w-[40px]',
      },
      enableSorting: false,
      enableHiding: false,
    }),
    columnHelper.accessor((row) => formatDateWithUserTimezone(new Date(row.serviceDate), userTimezone), {
      id: 'createdDate',
      header: 'Created Date',
      enableSorting: true,
      enableGrouping: true,
      meta: {
        filterType: 'date-range',
      },
    }),
    columnHelper.accessor((row) => formatTimeWithUserTimezone(new Date(row.serviceDate), userTimezone), {
      id: 'createdTime',
      header: 'Created Time',
      enableSorting: true,
      enableGrouping: false,
    }),
    columnHelper.accessor('asset', {
      header: 'Asset',
      cell: ({ getValue }) => {
        const value = getValue();
        return value ? <span>{EntityRefUtil.createAssetLabel(value)}</span> : <span className="text-secondary-text">-</span>;
      },
      enableGrouping: true,
      enableColumnFilter: true,
      getGroupingValue: (row) => (row.asset?.internalId ? EntityRefUtil.createAssetLabel(row.asset) : 'None'),
      meta: {
        columnGroup: ASSET_SECTION_LABEL,
        filterType: 'async-multi-select',
        getFilterOptionsFn: async (inputValue, value) => {
          const filters = inputValue ? { filters: { search: inputValue } } : undefined;
          const { data } = await (await ApiService.GetAssets({ limit: 100, filters: { ...filters, state: RecordState.Active } })).json();

          const resultsMap = new Map(data.map((asset) => [asset.id, { label: EntityRefUtil.createAssetLabel(asset), value: asset.id }]));

          if (value?.length) {
            const missingIds = Array.isArray(value) ? value.filter((id) => !resultsMap.has(id)) : [];
            if (missingIds.length) {
              const { data: missingAssets } = await (
                await ApiService.GetAssets({
                  limit: 100,
                  filters: { fields: { id: missingIds }, state: RecordState.Active },
                })
              ).json();

              for (const asset of missingAssets) {
                resultsMap.set(asset.id, { label: EntityRefUtil.createAssetLabel(asset), value: asset.id });
              }
            }
          }

          return Array.from(resultsMap.values());
        },
      },
    }),
    columnHelper.accessor('location', {
      header: 'Location',
      cell: ({ getValue }) => {
        const value = getValue();
        return value ? <span>{value.name}</span> : <span className="text-secondary-text">-</span>;
      },
      getGroupingValue: (row) => row.location?.name || 'None',
      enableGrouping: true,
      enableColumnFilter: true,
      meta: {
        filterType: 'async-multi-select',
        getFilterOptionsFn: async (inputValue, value) => {
          const filters = inputValue ? { filters: { search: inputValue } } : undefined;
          const { data } = await (await ApiService.GetLocations({ limit: 100, filters: { ...filters, state: RecordState.Active } })).json();

          const resultsMap = new Map(data.map((location) => [location.id, { label: location.name, value: location.id }]));

          if (value?.length) {
            const missingIds = Array.isArray(value) ? value.filter((id) => !resultsMap.has(id)) : [];
            if (missingIds.length) {
              const { data: missingLocations } = await (
                await ApiService.GetLocations({
                  limit: 100,
                  filters: { fields: { id: missingIds }, state: RecordState.Active },
                })
              ).json();

              for (const location of missingLocations) {
                resultsMap.set(location.id, { label: location.name, value: location.id });
              }
            }
          }

          return Array.from(resultsMap.values());
        },
      },
    }),
    columnHelper.accessor((row) => row.servicedBy ?? row.submittedBy, {
      id: 'servicedBy',
      header: 'Created By',
      cell: ({ row }) => {
        const scheduleForm = row.original;
        const servicedBy = scheduleForm.servicedBy ?? scheduleForm.submittedBy;
        const fullName = EntityRefUtil.createUserFullNameRef(servicedBy);
        const value = fullName || '-';
        const avatarBackgroundColorClass = getAvatarBackgroundColorClass(servicedBy?.firstName || '');

        const servicedByFullInitials = initials(fullName);
        return (
          <UserAvatarLabel
            value={value}
            operatedByFullInitials={servicedByFullInitials}
            avatarUrl={servicedBy && 'avatarUrl' in servicedBy ? (servicedBy.avatarUrl ?? '') : ''}
            avatarBackgroundColorClass={avatarBackgroundColorClass}
          />
        );
      },
      getGroupingValue: (row) => {
        const servicedBy = row.servicedBy ?? row.submittedBy;
        return EntityRefUtil.createUserFullNameRef(servicedBy, 'None');
      },
      enableGrouping: true,
      enableColumnFilter: true,
      meta: {
        filterType: 'async-multi-select',
        getFilterOptionsFn: async (inputValue, value) => {
          const filters = inputValue ? { filters: { search: inputValue } } : undefined;
          const { data: sitemateUserData } = await (
            await ApiService.GetUsers({ limit: 100, filters: { ...filters?.filters, userType: 'sitemate-users' } })
          ).json();
          const { data: userData } = await (await ApiService.GetUsers({ limit: 100, ...filters })).json();

          const resultsMap = new Map<string, { label: string; value: string }>();
          sitemateUserData.forEach((user) => {
            if (user.firstName || user.lastName) {
              resultsMap.set(user.id, { label: EntityRefUtil.createUserFullNameRef(user), value: user.id });
            }
          });
          userData.forEach((user) => {
            if (user.firstName || user.lastName) {
              resultsMap.set(user.id, { label: EntityRefUtil.createUserFullNameRef(user), value: user.id });
            }
          });

          if (value?.length) {
            const missingIds = Array.isArray(value) ? value.filter((id) => !resultsMap.has(id)) : [];
            if (missingIds.length) {
              const { data: missingSitemateUsers } = await (
                await ApiService.GetUsers({
                  limit: 100,
                  filters: { id: missingIds, userType: 'sitemate-users' },
                })
              ).json();
              const { data: missingUsers } = await (
                await ApiService.GetUsers({
                  limit: 100,
                  filters: { id: missingIds },
                })
              ).json();

              for (const user of missingSitemateUsers) {
                resultsMap.set(user.id, { label: EntityRefUtil.createUserFullNameRef(user), value: user.id });
              }
              for (const user of missingUsers) {
                if (user.firstName || user.lastName) {
                  resultsMap.set(user.id, { label: EntityRefUtil.createUserFullNameRef(user), value: user.id });
                }
              }
            }
          }

          return Array.from(resultsMap.values());
        },
      },
    }),
    columnHelper.accessor((row) => row.form?.name, {
      id: 'formName',
      header: 'Form Name',
      cell: ({ getValue }) => {
        const value = getValue();
        return value ? <span>{value}</span> : <span className="text-secondary-text">-</span>;
      },
      meta: {
        filterType: 'text',
      },
      enableColumnFilter: true,
      enableGrouping: true,
    }),
    columnHelper.accessor((row) => row.form?.number, {
      id: 'formNumber',
      header: 'Form ID',
      cell: ({ getValue }) => {
        const value = getValue();
        return value ? <span>{EntityRefUtil.createFormRef(value)}</span> : <span className="text-secondary-text">-</span>;
      },
      meta: {
        filterType: 'text',
      },
      enableColumnFilter: true,
      enableGrouping: true,
    }),
    columnHelper.accessor('asset.internalId', {
      id: 'assetInternalId',
      header: 'Asset ID',
      meta: { columnGroup: ASSET_SECTION_LABEL },
      enableGrouping: false,
      enableColumnFilter: false,
    }),
  ].filter((col): col is AccessorKeyColumnDef<GetScheduleFormsResponseDTO['data'][number]> => Boolean(col));
};

export const groupOptions = [
  { label: 'None', accessor: '' },
  ...columns()
    .filter((column) => {
      const availableGroupColumns = ['asset', 'location', 'servicedBy', 'createdDate', 'formName', 'formNumber'];
      return !column.accessorKey && column.id ? availableGroupColumns.includes(column.id) : availableGroupColumns.includes(column.accessorKey);
    })
    .map((column) => ({ label: column.header?.toString() || '', accessor: !column.accessorKey && column.id ? column.id : column.accessorKey })),
] satisfies { label: string; accessor: string }[];

export const FILTER_FIELD_MAP: Record<
  NonNullable<ReturnType<typeof columns>[number]['id'] | ReturnType<typeof columns>[number]['accessorKey']>,
  keyof NonNullable<NonNullable<GetScheduleFormsRequestDTO['filters']>['fields']>
> = {
  contact: 'contactId',
  formName: 'formName',
  formNumber: 'formNumber',
  location: 'locationId',
  asset: 'assetId',
  createdDate: 'createdDate',
  servicedBy: 'servicedById',
};
